import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { Toast, type ToastVariant } from '../components/Toast';
import { useLang } from '../lib/lang';

const BN_CPD = { back: 'Laman Utama', title: 'Pembangunan Profesional', subtitle: 'Continuous Professional Development', tabs: ['Kursus', 'Sejarah', 'Ringkasan'] as const, search: 'Cari', searchPlaceholder: 'Tajuk, penyedia…', allCat: 'Semua Kategori', enrolBtn: '+ Daftar', enrolTitle: 'Daftar Kursus', startDate: 'Tarikh Mula', endDate: 'Tarikh Tamat', venue: 'Tempat', confirm: 'Sahkan Pendaftaran', confirming: 'Mendaftar…', cancel: 'Batal', hours: 'jam', milestone: '🏆 100 Jam CPD Dicapai!', totalHours: 'Jumlah Jam', emptyTitle: 'Tiada Rekod', emptyBody: 'Tiada data dijumpai.', retry: 'Cuba Lagi', prev: '← Sebelum', next: 'Seterus →' };
const EN_CPD = { back: 'Home', title: 'Professional Development', subtitle: 'Continuous Professional Development', tabs: ['Courses', 'History', 'Summary'] as const, search: 'Search', searchPlaceholder: 'Title, provider…', allCat: 'All Categories', enrolBtn: '+ Enrol', enrolTitle: 'Enrol in Course', startDate: 'Start Date', endDate: 'End Date', venue: 'Venue', confirm: 'Confirm Enrolment', confirming: 'Enrolling…', cancel: 'Cancel', hours: 'hrs', milestone: '🏆 100-Hour CPD Milestone Achieved!', totalHours: 'Total Hours', emptyTitle: 'No Records', emptyBody: 'No data found.', retry: 'Retry', prev: '← Previous', next: 'Next →' };

interface Props { user: AuthUser }

interface Course {
  id: string; code: string; title_en: string; title_ms: string;
  provider: string; category: string; cpd_hours: string; mode: string;
  description_ms: string | null; is_approved: boolean;
}

interface Summary {
  total_hours: string; milestone_100: boolean;
  hours_by_category: Record<string, number>;
  milestone_achieved_at: string | null;
}

interface Enrolment {
  id: string; course_id: string; status: string;
  start_date: string; end_date: string; completion_date: string | null;
  cpd_hours_earned: string | null; certificate_url: string | null;
  title_ms: string; title_en: string; category: string;
  provider: string; cpd_hours: string; mode: string;
  school_year_name: string | null;
}

const CATEGORIES = ['PEDAGOGY','CONTENT','LEADERSHIP','ICT','WELFARE','RELIGIOUS'];
const CAT_LABELS: Record<string, string> = {
  PEDAGOGY: 'Pedagogi', CONTENT: 'Kandungan', LEADERSHIP: 'Kepimpinan',
  ICT: 'ICT', WELFARE: 'Kebajikan', RELIGIOUS: 'Agama',
};
const CAT_COLORS: Record<string, string> = {
  PEDAGOGY: '#60a5fa', CONTENT: '#4ade80', LEADERSHIP: colors.primary.yellow,
  ICT: '#a78bfa', WELFARE: '#fb923c', RELIGIOUS: '#f87171',
};

const STATUS_CFG: Record<string, { label: string; color: string }> = {
  ENROLLED:  { label: 'Berdaftar',    color: '#60a5fa' },
  ATTENDED:  { label: 'Hadir',        color: '#facc15' },
  COMPLETED: { label: 'Selesai ✓',   color: '#4ade80' },
  NO_SHOW:   { label: 'Tidak Hadir',  color: '#f87171' },
  WITHDRAWN: { label: 'Ditarik Balik', color: '#9ca3af' },
};

function getSchoolId(user: AuthUser): string | null {
  if (user.roles.some((r) => r.role === 'super_admin' && !r.schoolId)) return null;
  return user.roles.find((r) => r.schoolId)?.schoolId ?? null;
}

export function CPDPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN_CPD : BN_CPD;
  const [searchParams] = useSearchParams();
  const educatorId = searchParams.get('educatorId') ?? '';

  const [tab, setTab] = useState<'courses' | 'history' | 'summary'>('courses');

  const [courses,     setCourses]     = useState<Course[]>([]);
  const [enrolments,  setEnrolments]  = useState<Enrolment[]>([]);
  const [summaries,   setSummaries]   = useState<Summary[]>([]);

  const [coursesLoading, setCoursesLoading] = useState(false);
  const [histLoading,    setHistLoading]    = useState(false);
  const [sumLoading,     setSumLoading]     = useState(false);

  const [coursesError, setCoursesError] = useState<string | null>(null);
  const [histError,    setHistError]    = useState<string | null>(null);
  const [sumError,     setSumError]     = useState<string | null>(null);

  const [searchInput, setSearchInput]  = useState('');
  const [filterCat,   setFilterCat]    = useState('');

  const [enrollingId, setEnrollingId]  = useState<string | null>(null);
  const [enrolForm,   setEnrolForm]    = useState({ startDate: '', endDate: '', venue: '' });
  const [enrolling,   setEnrolling]    = useState(false);
  const [toast,       setToast]        = useState<{ message: string; variant: ToastVariant } | null>(null);

  const loadCourses = useCallback(async () => {
    setCoursesLoading(true); setCoursesError(null);
    try {
      const params = new URLSearchParams({ approvedOnly: 'true' });
      if (filterCat)           params.set('category', filterCat);
      if (searchInput.trim())  params.set('search', searchInput.trim());
      setCourses(await apiGet<Course[]>(`/api/cpd/courses?${params}`));
    } catch (err) {
      setCoursesError(err instanceof Error ? err.message : 'Ralat memuatkan kursus.');
    } finally { setCoursesLoading(false); }
  }, [filterCat, searchInput]);

  const loadHistory = useCallback(async () => {
    if (!educatorId) { setHistError('Sila pilih pendidik. / Select an educator first.'); return; }
    setHistLoading(true); setHistError(null);
    try {
      setEnrolments(await apiGet<Enrolment[]>(`/api/cpd/educators/${educatorId}/history`));
    } catch (err) {
      setHistError(err instanceof Error ? err.message : 'Ralat memuatkan sejarah.');
    } finally { setHistLoading(false); }
  }, [educatorId]);

  const loadSummary = useCallback(async () => {
    if (!educatorId) { setSumError('Sila pilih pendidik. / Select an educator first.'); return; }
    setSumLoading(true); setSumError(null);
    try {
      const data = await apiGet<{ summaries: Summary[]; enrolments: Enrolment[] }>(
        `/api/cpd/educators/${educatorId}/summary`,
      );
      setSummaries(data.summaries);
    } catch (err) {
      setSumError(err instanceof Error ? err.message : 'Ralat memuatkan ringkasan.');
    } finally { setSumLoading(false); }
  }, [educatorId]);

  useEffect(() => { if (tab === 'courses') void loadCourses(); }, [tab, loadCourses]);
  useEffect(() => { if (tab === 'history') void loadHistory(); }, [tab, loadHistory]);
  useEffect(() => { if (tab === 'summary') void loadSummary(); }, [tab, loadSummary]);

  async function handleEnrol(courseId: string) {
    if (!educatorId) {
      setToast({ variant: 'error', message: 'URL mesti menyertakan ?educatorId=UUID. / URL must include ?educatorId=UUID.' });
      return;
    }
    if (!enrolForm.startDate) {
      setToast({ variant: 'error', message: 'Sila pilih tarikh mula. / Select a start date.' }); return;
    }
    if (!enrolForm.endDate) {
      setToast({ variant: 'error', message: 'Sila pilih tarikh tamat. / Select an end date.' }); return;
    }
    if (enrolForm.endDate < enrolForm.startDate) {
      setToast({ variant: 'error', message: 'Tarikh tamat mesti selepas tarikh mula. / End date must be after start date.' }); return;
    }

    setEnrolling(true);
    try {
      // Use a placeholder school year ID for demo — in production, fetch from school years
      await apiPost('/api/cpd/enrolments', {
        educatorId,
        courseId,
        schoolYearId: '00000000-0000-0000-0000-000000000001', // demo placeholder
        startDate:    enrolForm.startDate,
        endDate:      enrolForm.endDate,
        venue:        enrolForm.venue.trim() || undefined,
      });
      setToast({ variant: 'success', message: 'Pendidik berjaya didaftarkan ke kursus. / Educator enrolled successfully.' });
      setEnrollingId(null);
      setEnrolForm({ startDate: '', endDate: '', venue: '' });
      void loadHistory();
    } catch (err) {
      setToast({ variant: 'error', message: err instanceof Error ? err.message : 'Gagal mendaftar. / Failed to enrol.' });
    } finally { setEnrolling(false); }
  }

  const latestSummary = summaries[0];
  const totalHours    = parseFloat(latestSummary?.total_hours ?? '0') || 0;
  const milestoneReached = latestSummary?.milestone_100 === true;

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>

          {/* Nav */}
          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>← {UI.back}</Link>
            {educatorId && (
              <Link to={`/admin/educators/${educatorId}`} style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none', marginLeft: spacing.md }}>
                ← Profil Pendidik
              </Link>
            )}
          </div>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, gap: spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                📚 CPD — {UI.title}
              </h1>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                Continuing Professional Development
                {educatorId ? '' : ' — pilih pendidik untuk melihat sejarah / select an educator for history'}
              </p>
            </div>
            {/* Milestone badge */}
            {milestoneReached && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: spacing.sm,
                backgroundColor: 'rgba(247,208,0,0.12)', border: `1px solid ${colors.primary.yellow}`,
                borderRadius: borderRadius.xl, padding: `${spacing.xs} ${spacing.md}`,
              }}>
                <span style={{ fontSize: '24px' }}>🏆</span>
                <div>
                  <p style={{ margin: 0, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, color: colors.primary.yellow }}>100 Jam CPD!</p>
                  <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>Pencapaian dicapai / Milestone reached</p>
                </div>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, marginBottom: spacing.lg, backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, overflow: 'hidden' }}>
            {(['courses','history','summary'] as const).map((t) => (
              <button key={t} onClick={() => setTab(t)} style={{
                flex: 1, padding: `${spacing.sm} ${spacing.xs}`, background: 'none',
                borderBottom: tab === t ? `2px solid ${colors.primary.yellow}` : '2px solid transparent',
                borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                color: tab === t ? colors.primary.yellow : colors.neutral.gray400,
                fontWeight: tab === t ? typography.fontWeight.semibold : typography.fontWeight.medium,
                fontSize: typography.fontSize.sm, cursor: 'pointer',
                fontFamily: typography.fontFamily.body,
              }}>
                {t === 'courses' ? UI.tabs[0] : t === 'history' ? UI.tabs[1] : UI.tabs[2]}
              </button>
            ))}
          </div>

          {/* ── Courses tab ─────────────────────────────────────────────────── */}
          {tab === 'courses' && (
            <>
              {/* Filters */}
              <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, marginBottom: spacing.lg }}>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: '2 1 200px' }}>
                    <label style={labelSt}>Carian / Search</label>
                    <input
                      type="text" value={searchInput}
                      placeholder="Tajuk, kod, penganjur…"
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && void loadCourses()}
                      style={inputSt()}
                    />
                  </div>
                  <div style={{ flex: '1 1 140px' }}>
                    <label style={labelSt}>Kategori</label>
                    <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} style={inputSt()}>
                      <option value="">Semua</option>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
                    </select>
                  </div>
                  <Button variant="primary" size="sm" onClick={() => void loadCourses()} disabled={coursesLoading}>
                    {coursesLoading ? <Spinner size="sm" /> : 'Cari'}
                  </Button>
                </div>
              </div>

              {coursesError && <ErrorBox msg={coursesError} onRetry={() => void loadCourses()} />}
              {coursesLoading && courses.length === 0 && <LoadingBox />}
              {!coursesLoading && !coursesError && courses.length === 0 && (
                <EmptyBox icon="📚" title={UI.emptyTitle} body={UI.emptyBody} />
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                {courses.map((c) => {
                  const isExpanded = enrollingId === c.id;
                  return (
                    <div key={c.id} style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, overflow: 'hidden', boxShadow: shadows.lg }}>
                      <div style={{ padding: spacing.lg, display: 'flex', alignItems: 'flex-start', gap: spacing.md, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs, flexWrap: 'wrap' }}>
                            <Pill label={CAT_LABELS[c.category] ?? c.category} color={CAT_COLORS[c.category] ?? '#9ca3af'} />
                            <Pill label={c.mode} color="#60a5fa" />
                            <Pill label={`${c.cpd_hours} jam`} color="#4ade80" />
                          </div>
                          <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{c.title_ms}</p>
                          <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                            {c.code} · {c.provider}
                          </p>
                          {c.description_ms && (
                            <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400, lineHeight: '1.5' }}>
                              {c.description_ms}
                            </p>
                          )}
                        </div>
                        {educatorId && (
                          <Button
                            variant={isExpanded ? 'ghost' : 'secondary'} size="sm"
                            onClick={() => { setEnrollingId(isExpanded ? null : c.id); setEnrolForm({ startDate: '', endDate: '', venue: '' }); }}
                            disabled={enrolling}
                          >
                            {isExpanded ? 'Batal' : '+ Daftar'}
                          </Button>
                        )}
                      </div>

                      {/* Enrol form */}
                      {isExpanded && (
                        <div style={{ padding: spacing.lg, borderTop: `1px solid ${colors.neutral.gray800}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                          <p style={{ margin: `0 0 ${spacing.sm}`, fontSize: typography.fontSize.sm, color: colors.neutral.white, fontWeight: typography.fontWeight.semibold }}>
                            Daftar Pendidik ke Kursus Ini
                          </p>
                          <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.sm }}>
                            <div style={{ flex: '1 1 130px' }}>
                              <label style={labelSt}>Tarikh Mula *</label>
                              <input type="date" value={enrolForm.startDate}
                                onChange={(e) => setEnrolForm((f) => ({ ...f, startDate: e.target.value }))}
                                max={enrolForm.endDate || undefined} disabled={enrolling} style={inputSt()} />
                            </div>
                            <div style={{ flex: '1 1 130px' }}>
                              <label style={labelSt}>Tarikh Tamat *</label>
                              <input type="date" value={enrolForm.endDate}
                                onChange={(e) => setEnrolForm((f) => ({ ...f, endDate: e.target.value }))}
                                min={enrolForm.startDate || undefined} disabled={enrolling} style={inputSt()} />
                            </div>
                            <div style={{ flex: '2 1 200px' }}>
                              <label style={labelSt}>Tempat / Venue</label>
                              <input type="text" value={enrolForm.venue}
                                onChange={(e) => setEnrolForm((f) => ({ ...f, venue: e.target.value }))}
                                placeholder="cth: Pusat Sumber Negara"
                                maxLength={300} disabled={enrolling} style={inputSt()} />
                            </div>
                          </div>
                          <Button variant="primary" size="sm" onClick={() => void handleEnrol(c.id)} disabled={enrolling} loading={enrolling}>
                            {enrolling ? 'Mendaftar…' : 'Sahkan Pendaftaran / Confirm Enrolment'}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── History tab ─────────────────────────────────────────────────── */}
          {tab === 'history' && (
            <>
              {histError && <ErrorBox msg={histError} onRetry={() => void loadHistory()} />}
              {histLoading && <LoadingBox />}
              {!histLoading && !histError && enrolments.length === 0 && (
                <EmptyBox icon="📋" title={UI.emptyTitle} body={UI.emptyBody} />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                {enrolments.map((e) => {
                  const sc = STATUS_CFG[e.status] ?? { label: e.status, color: '#9ca3af' };
                  return (
                    <div key={e.id} style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, border: `1px solid ${e.status === 'COMPLETED' ? 'rgba(74,222,128,0.2)' : colors.neutral.gray800}` }}>
                      <div style={{ display: 'flex', gap: spacing.md, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs, flexWrap: 'wrap' }}>
                            <Pill label={CAT_LABELS[e.category] ?? e.category} color={CAT_COLORS[e.category] ?? '#9ca3af'} />
                            <Pill label={sc.label} color={sc.color} />
                            {e.cpd_hours_earned && <Pill label={`${e.cpd_hours_earned} jam`} color="#4ade80" />}
                          </div>
                          <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{e.title_ms}</p>
                          <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                            {e.provider} · {new Date(e.start_date).toLocaleDateString('ms-BN')}
                            {e.end_date ? ` – ${new Date(e.end_date).toLocaleDateString('ms-BN')}` : ''}
                            {e.school_year_name ? ` · ${e.school_year_name}` : ''}
                          </p>
                        </div>
                        {e.certificate_url && (
                          <a href={e.certificate_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm">🏅 Sijil</Button>
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── Summary tab ─────────────────────────────────────────────────── */}
          {tab === 'summary' && (
            <>
              {sumError && <ErrorBox msg={sumError} onRetry={() => void loadSummary()} />}
              {sumLoading && <LoadingBox />}
              {!sumLoading && !sumError && summaries.length === 0 && (
                <EmptyBox icon="📊" title={UI.emptyTitle} body={UI.emptyBody} />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
                {summaries.map((s, i) => (
                  <div key={i} style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, border: s.milestone_100 ? `1px solid ${colors.primary.yellow}44` : `1px solid ${colors.neutral.gray800}` }}>
                    {s.milestone_100 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md, padding: spacing.sm, backgroundColor: 'rgba(247,208,0,0.08)', borderRadius: borderRadius.lg }}>
                        <span style={{ fontSize: '20px' }}>🏆</span>
                        <p style={{ margin: 0, color: colors.primary.yellow, fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.sm }}>
                          Pencapaian 100 Jam CPD
                          {s.milestone_achieved_at ? ` · ${new Date(s.milestone_achieved_at).toLocaleDateString('ms-BN')}` : ''}
                        </p>
                      </div>
                    )}

                    {/* Hours meter */}
                    <div style={{ marginBottom: spacing.lg }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>Jumlah Jam CPD</span>
                        <span style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: s.milestone_100 ? colors.primary.yellow : colors.neutral.white }}>
                          {parseFloat(s.total_hours).toFixed(1)} / 100
                        </span>
                      </div>
                      <div style={{ height: '8px', backgroundColor: colors.neutral.gray800, borderRadius: borderRadius.full, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${Math.min(100, (parseFloat(s.total_hours) / 100) * 100)}%`,
                          backgroundColor: s.milestone_100 ? colors.primary.yellow : '#60a5fa',
                          borderRadius: borderRadius.full, transition: 'width 0.5s ease',
                        }} />
                      </div>
                    </div>

                    {/* By category */}
                    {s.hours_by_category && Object.keys(s.hours_by_category).length > 0 && (
                      <div>
                        <p style={{ margin: `0 0 ${spacing.sm}`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400, fontWeight: typography.fontWeight.semibold }}>
                          PECAHAN KATEGORI / BY CATEGORY
                        </p>
                        <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                          {Object.entries(s.hours_by_category).map(([cat, hrs]) => (
                            <div key={cat} style={{ backgroundColor: `${CAT_COLORS[cat] ?? '#9ca3af'}18`, borderRadius: borderRadius.lg, padding: `${spacing.xs} ${spacing.sm}`, textAlign: 'center', minWidth: '80px' }}>
                              <p style={{ margin: 0, fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: CAT_COLORS[cat] ?? '#9ca3af' }}>{Number(hrs).toFixed(1)}</p>
                              <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{CAT_LABELS[cat] ?? cat}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

// ── Shared helpers ────────────────────────────────────────────────────────────
function Pill({ label, color }: { label: string; color: string }) {
  return (
    <span style={{ display: 'inline-block', padding: `1px ${spacing.xs}`, backgroundColor: `${color}22`, color, borderRadius: borderRadius.full, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold }}>
      {label}
    </span>
  );
}
function ErrorBox({ msg, onRetry }: { msg: string; onRetry: () => void }) {
  return (
    <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.md }}>
      <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{msg}</p>
      <Button variant="ghost" size="sm" onClick={onRetry} style={{ marginTop: spacing.xs }}>Cuba Lagi / Retry</Button>
    </div>
  );
}
function LoadingBox() { return <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>; }
function EmptyBox({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, textAlign: 'center', marginBottom: spacing.md }}>
      <div style={{ fontSize: '40px', marginBottom: spacing.sm }}>{icon}</div>
      <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{title}</p>
      <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>{body}</p>
    </div>
  );
}
const labelSt: React.CSSProperties = { display: 'block', fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginBottom: spacing.xs };
function inputSt(): React.CSSProperties {
  return {
    width: '100%', backgroundColor: colors.neutral.black,
    border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md,
    color: colors.neutral.white, padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.fontSize.sm, outline: 'none',
    boxSizing: 'border-box', fontFamily: typography.fontFamily.body,
  };
}
